// Pure CSV serializer shared by the report pages (Daily Attendance, Grooming,
// Mood, Random Attendance). Each of those pages built the export string with the
// identical snippet:
//
//   const csv = [
//     headers.join(','),
//     ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
//   ].join('\n');
//
// Every cell is wrapped in double quotes and joined with commas, header row first.
//
// ESCAPING (added): a cell containing a double quote must double it (RFC 4180), or the
// quote terminates the field early and Excel shifts every later column on that row. This
// went unnoticed while cells were system-generated (codes, names, dates), but exports now
// carry FREE TEXT — employee remarks / call notes — where a quote or a newline is normal.
// Newlines and carriage returns are folded to spaces so one note can't split into two rows.
const csvCell = (cell: string): string =>
  `"${String(cell ?? '').replace(/\r?\n|\r/g, ' ').replace(/"/g, '""')}"`;

// Headers USED to be developer-authored constants, and were joined raw on that basis.
// They aren't any more: the AI custom-report export builds its header row from
// `columnDisplay[c] ?? c` (CustomReportPanel.handleDownload). `columnDisplay` values are
// AI-authored templates — coerceColumnDisplay validates the alias KEY against
// /^[a-z_][a-z0-9_]*$/ but accepts any 200-char string as the VALUE, and renderTemplate
// passes text that isn't a {{...}} token through untouched. So a label like `Sales, Net`
// reaches this function, and an unquoted join silently shifts every column after it.
//
// Quoted only WHEN NECESSARY rather than always, so the long-standing exports whose
// headers are plain identifiers keep their exact current bytes — the existing
// CustomReportPanel.download.test.tsx assertions on the raw header line are left
// meaningful as regression evidence instead of being rewritten to match new output.
const NEEDS_QUOTING_RE = /[",\r\n]/;
const csvHeader = (header: string): string => {
  const s = String(header ?? '');
  return NEEDS_QUOTING_RE.test(s) ? csvCell(s) : s;
};

export function buildCsv(headers: string[], rows: Array<Array<string>>): string {
  return [
    headers.map(csvHeader).join(','),
    ...rows.map(row => row.map(csvCell).join(',')),
  ].join('\n');
}

// The UTF-8 byte-order mark. Excel opens a .csv in the PC's legacy code page unless the
// file starts with this, so anything beyond ASCII — an en dash, say — comes out garbled.
// OPT-IN: an export prefixes its own CSV with it. buildCsv and downloadCsv never add it,
// so every export that does not ask keeps the bytes it has always had.
export const CSV_UTF8_BOM = '\uFEFF';

// Triggers a browser download of `csv` as a text/csv file named `filename`.
// Extracted verbatim from the report pages' identical Blob -> object-URL -> anchor
// -> click -> revoke tail (only the download filename varied). DOM side-effect only.
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

/**
 * Save an already-built Blob (e.g. an .xlsx body streamed from the API) under a
 * filename. Same anchor-click mechanism as downloadCsv, but the bytes come from
 * the server rather than being serialised here — which is the point for large
 * exports: the rows never exist as JavaScript objects in the tab.
 */
export function downloadBlob(filename: string, blob: Blob): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}
